import api from "@/http/api";
import { useMutation, useQuery, useInfiniteQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";

const getOrders = (search?: string, page: number = 1, limit: number = 50, status?: string) => {
  const query = new URLSearchParams();
  if (search) query.append('search', search);
  if (status) query.append('status', status);
  query.append('page', page.toString());
  query.append('limit', limit.toString());
  return api.get(`/order?${query.toString()}`);
};

export const useOrder = (search?: string, page: number = 1, limit: number = 50, options?: any) => {
  return useQuery({
    queryKey: ["orders", search, page, limit],
    queryFn: async () => {
      return await getOrders(search, page, limit);
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

// `status` scopes the query to a single Kanban column so each column owns its
// own cursor. Without it every column shares one window over the whole order
// list and sparse statuses stay empty until you page past everything newer.
export const useInfiniteOrder = (search?: string, limit: number = 50, status?: string, options?: any) => {
  return useInfiniteQuery({
    queryKey: ["orders-infinite", search, limit, status],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getOrders(search, pageParam as number, limit, status);
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      const { currentPage, totalPages } = lastPage.pagination;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    ...options
  });
};

const updateOrder = (
  order_id: string,
  customer_id: string,
  updates: { labels?: string[]; status?: string; is_new_update?: boolean; orderRef: string }
) => {
  return api.post(`/order/update/${order_id}/${customer_id}`, {
    ...updates,
    labels: updates.labels ? JSON.stringify(updates.labels) : undefined,
  });
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      order_id,
      customer_id,
      updates,
    }: {
      order_id: string;
      customer_id: string;
      updates: { labels?: string[]; status?: string; is_new_update?: boolean; orderRef: string };
    }) => updateOrder(order_id, customer_id, updates),
    onSuccess: () => {
      // "orders" does not prefix-match "orders-infinite" -- they are separate
      // root keys -- so the Kanban board needs its own line, or an edit made
      // from the table never reaches the board's per-column caches.
      // ponytail: refetches every loaded page of every column. Fine at one
      // page per column; move to a targeted setQueryData on source/target
      // status if boards get deep.
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders-infinite"] });
      toast.success("Order updated successfully");
    },
    onError: () => {
      toast.error("Failed to update order");
    },
  });
};

const getOrderInst = (order_id: string) => {
  return api.get(`/admin/order/instruction/${order_id}`);
};

export const useOrderInst = (order_id: string) => {
  return useQuery({
    queryKey: ["order-instruction", order_id],
    queryFn: async () => {
      return await getOrderInst(order_id);
    },
    enabled: !!order_id,
  });
};
